import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Award, Users, Heart, Target } from 'lucide-react';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';
import { mission, vision, organizationInfo } from '../data/mock';

const About = () => {
  return (
    <div>
      <SEO 
        title="About Us - Youth Nonprofit Since 2009"
        description="Mentors in Motion is a St. Louis 501(c)(3) nonprofit empowering youth since 2009 through mentoring, tutoring, and creative learning."
        keywords="about mentors in motion, St. Louis nonprofit history, youth development organization, 501c3 charity Missouri, education nonprofit"
        canonicalUrl="/about"
      />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gray-900 overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_40%,rgba(227,27,35,0.3),transparent_50%)]" />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-2xl sm:text-3xl font-bold text-white mb-6">
              About Mentors in Motion
            </h1>
            <p className="text-base text-gray-300 leading-relaxed">
              Mentors in Motion is a community-rooted nonprofit that has been empowering under-resourced youth through mentorship, education, and creative programs since 2009. We help scholars strengthen their reading and math skills, build confidence with caring adult mentors, and discover talents in areas like music, media, fashion, cosmetology, gaming, and entrepreneurship. By combining academic support with real-world experiences, we equip young people to succeed in school today and step into leadership, career, and business opportunities tomorrow.
            </p>
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-sm font-semibold text-red-600 uppercase tracking-wider mb-3">
                Our Story
              </h2>
              <h3 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
                From One Classroom to a Movement
              </h3>
              
              <div className="space-y-4 text-gray-600 leading-relaxed">
                <p>
                  Mentors in Motion was founded in 2009 by educator and musician Alandon Pitts, who started with a simple but powerful idea: if students don't all learn the same way, teaching shouldn't look the same for everyone. In his St. Louis classroom, he saw scholars who were bright and capable but disengaged because traditional methods didn't connect with them.
                </p>
                <p>
                  Drawing on his background in music production, Alandon began turning lessons into songs, hooks, and creative activities making learning feel more like a studio session than a worksheet. The impact on his students was immediate: they paid attention longer, participated more, remembered what they were taught, and started to show higher test scores and better attendance. Scholars who had been written off as "unreachable" suddenly saw themselves as capable learners.
                </p>
                <p>
                  What began as one teacher's creative solution has grown into Mentors in Motion, a nationally recognized nonprofit that has now served more than 15,000 youth across the St. Louis area. Today, scholars benefit from that same spirit of innovation through mentoring, tutoring, and creative learning tracks in music, media, fashion, cosmetology, podcasting, e-gaming, and more.
                </p>
                <p>
                  Along the way, Mentors in Motion has been honored by the City of St. Louis Mayor's Office and the Missouri House of Representatives for its positive community impact. Most importantly, the model continues to do what it did in that first classroom: help young people feel seen, unlock their potential, and change the trajectory of their education and their lives.
                </p>
              </div>

              <div className="mt-8 flex items-center gap-4">
                <Link to="/team">
                  <Button className="bg-red-600 hover:bg-red-700 text-white">
                    Meet Our Team
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            </div>

            <div className="relative">
              <div className="absolute inset-0 bg-red-600/10 rounded-3xl transform -rotate-3" />
              <img
                src="https://customer-assets.emergentagent.com/job_mentorsinmotion/artifacts/2rzm9q01_b62ce6_f38679bfa7474d969d6831a6816753dd.jpg"
                alt="Students in creative learning environment"
                className="relative rounded-3xl shadow-2xl w-full object-cover aspect-[4/3]"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Mission, Vision, Tagline */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              What Drives Us
            </h2>
            <p className="text-4xl font-bold text-red-600 italic">
              "{organizationInfo.tagline}"
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white p-8 rounded-2xl shadow-lg">
              <div className="w-14 h-14 bg-red-100 rounded-xl flex items-center justify-center mb-6">
                <Target className="w-7 h-7 text-red-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Our Mission</h3>
              <p className="text-gray-600 leading-relaxed">
                {mission}
              </p>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-lg">
              <div className="w-14 h-14 bg-gray-100 rounded-xl flex items-center justify-center mb-6">
                <Award className="w-7 h-7 text-gray-700" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Our Vision</h3>
              <p className="text-gray-600 leading-relaxed">
                {vision}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Community Leadership */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1">
              <img
                src="https://customer-assets.emergentagent.com/job_mentorsinmotion/artifacts/wsue93ti_MIM%202.jpg"
                alt="Community leadership meeting"
                className="rounded-3xl shadow-2xl w-full object-cover aspect-[4/3]"
              />
            </div>
            
            <div className="order-1 lg:order-2">
              <h2 className="text-sm font-semibold text-red-600 uppercase tracking-wider mb-3">
                Community-Rooted Leadership
              </h2>
              <h3 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
                Led by Those Who Understand
              </h3>
              <p className="text-gray-600 leading-relaxed mb-4">
                Mentors in Motion is led by passionate local leaders who reflect the communities they serve. Our team brings diverse backgrounds and lived experiences from growing up in the same neighborhoods to navigating similar schools and challenges which allows us to design programming that is culturally relevant, respectful, and effective for our scholars.
              </p>
              <p className="text-gray-600 leading-relaxed mb-4">
                Because our leaders understand what our young people are facing, they create spaces where students feel seen, heard, and safe to be themselves. This builds trust, which is the foundation for real growth in academics, behavior, and confidence.
              </p>
              <p className="text-gray-600 leading-relaxed mb-4">
                Mentors in Motion works hand-in-hand with schools, families, and community partners to ensure our programs respond to real needs, not assumptions. By listening to principals, teachers, parents, and students, we are able to tailor our mentoring, tutoring, and creative learning experiences so they fit into the school day, support classroom goals, and address what scholars are actually struggling with.
              </p>
              <p className="text-gray-600 leading-relaxed">
                This community-driven approach has been key to our success and growth over the past 15+ years. It's also what makes our programs so powerful for scholars: they are led by people who understand their world and are invested in helping them write a different story for their future.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Impact & Equity Focus */}
      <section className="py-20 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-sm font-semibold text-red-400 uppercase tracking-wider mb-3">
              Impact & Equity Focus
            </h2>
            <h3 className="text-3xl sm:text-4xl font-bold mb-6">
              Breaking Cycles, Building Futures
            </h3>
            <p className="text-xl text-gray-300 leading-relaxed mb-8">
              By investing in our youth today, we are creating the community leaders of tomorrow by breaking cycles of poverty and inequity through education, ultimately creating a safer, more thriving communities for all.
            </p>
            <p className="text-gray-400 leading-relaxed mb-10">
              Our work aligns with the philosophies of high-capacity philanthropists who focus on systemic equity and long-term, sustainable change. We're not just providing programs we're building pathways to generational transformation.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/impact">
                <Button size="lg" className="bg-red-600 hover:bg-red-700 text-white">
                  See Our Impact
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <Link to="/donate">
                <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10">
                  <Heart className="w-5 h-5 mr-2" />
                  Support Our Mission
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
